import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { inferRouterOutputs } from '@trpc/server';
import UserLayout from '@/components/Layout/UserLayout';
import { trpc, trpcClient } from '@/api/trpc';
import { Badge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { showToast } from '@/components/Layout/Toast';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';
import AIWorkApprovalRow from '@/components/AIWorkbench/AIWorkApprovalRow';
import { formatApprovalTimestamp, humanizeAIWorkLabel } from '@/components/AIWorkbench/helpers';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type AIWorkApproval = RouterOutputs['experiment']['listAIWorkApprovals'][number];

const getTargetDescriptor = (approval: AIWorkApproval): string => {
    if (approval.strategyId != null) return `strategy #${approval.strategyId}`;
    if (approval.experimentId != null) return `experiment #${approval.experimentId}`;
    return humanizeAIWorkLabel(approval.targetType);
};

const getApprovalSourceLink = (approval: AIWorkApproval): { to: string; label: string } | null => {
    if (approval.strategyId != null) return { to: `/user/tradingStrategy/${approval.strategyId}`, label: 'View strategy' };
    if (approval.experimentId != null) return { to: `/user/experimentResults/${approval.experimentId}`, label: 'View experiment' };
    return null;
};

const AIApprovals = () => {
    const { sessionPolicy } = useAuth();
    const queryClient = useQueryClient();

    const approvalsQuery = useQuery({
        ...trpc.experiment.listAIWorkApprovals.queryOptions({ statuses: ['requested'] }),
        enabled: sessionPolicy.canAccessAdminRoutes,
        refetchInterval: 5000,
        staleTime: 5000
    });

    const decideMutation = useMutation({
        mutationFn: async ({ approval, status }: { approval: AIWorkApproval; status: 'approved' | 'rejected' }) => {
            if (status === 'approved' && approval.approvalType === 'promote_screener' && approval.strategyId != null) {
                const promotion = await trpcClient.screener.optimizer.approveAndCreateStrategyVariant.mutate({
                    approvalId: approval.id
                });

                return {
                    status: 'executed',
                    strategyVariantStatus: promotion.strategyVariantStatus,
                    strategyVariantId: promotion.createdStrategyVariantId ?? promotion.reusedStrategyVariantId,
                    screenerRuleId: promotion.screenerRuleId,
                    strategyVariantErrorMessage: promotion.strategyVariantErrorMessage
                };
            }

            const decided = await trpcClient.experiment.decideAIWorkApproval.mutate({ approvalId: approval.id, status });
            return {
                status: decided.status,
                strategyVariantStatus: null,
                strategyVariantId: null,
                screenerRuleId: null,
                strategyVariantErrorMessage: null
            };
        },
        onSuccess: async (result) => {
            const message =
                result.strategyVariantStatus === 'created'
                    ? `Approval completed and strategy variant ${result.strategyVariantId} created`
                    : result.strategyVariantStatus === 'reused'
                      ? `Approval completed and strategy variant ${result.strategyVariantId} reused`
                      : result.strategyVariantStatus === 'blocked'
                        ? `Approval completed and screener rule ${result.screenerRuleId} created, but variant creation failed: ${result.strategyVariantErrorMessage ?? 'unknown error'}`
                        : `AI approval ${result.status}`;

            showToast(message, result.strategyVariantStatus === 'blocked' ? 'warning' : 'success');
            await queryClient.invalidateQueries(trpc.experiment.listAIWorkApprovals.pathFilter());
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Failed to update AI approval';
            showToast(message, 'error');
        }
    });

    const groupedApprovals = useMemo(() => {
        const groups = new Map<string, AIWorkApproval[]>();

        for (const approval of approvalsQuery.data ?? []) {
            const existing = groups.get(approval.taskKind);
            if (existing) existing.push(approval);
            else groups.set(approval.taskKind, [approval]);
        }

        return [...groups.entries()];
    }, [approvalsQuery.data]);

    const renderApprovalContext = (approval: AIWorkApproval) => {
        const link = getApprovalSourceLink(approval);
        const toolLabel = approval.externalToolLabel ?? humanizeAIWorkLabel(approval.externalTool);

        const artifactLabel =
            approval.artifactType != null
                ? `${humanizeAIWorkLabel(approval.artifactType)}${approval.artifactName ? ` · ${approval.artifactName}` : ''}`
                : null;

        return (
            <div className="flex flex-col gap-0.5 text-xs text-zinc-400">
                <div>
                    <span className="text-zinc-500">Target:</span> {getTargetDescriptor(approval)} · via {toolLabel}
                </div>
                {artifactLabel ? (
                    <div className="truncate" title={approval.artifactSummary ?? artifactLabel}>
                        <span className="text-zinc-500">Artifact:</span> {artifactLabel}
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span>
                        <span className="text-zinc-500">Requested:</span> {formatApprovalTimestamp(approval.createdAt)}
                    </span>
                    {link ? (
                        <Link className="text-blue-400 hover:text-blue-300" to={link.to}>
                            {link.label}
                        </Link>
                    ) : null}
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (approvalsQuery.isLoading) {
            return (
                <div className="flex justify-center py-16">
                    <DataLoadingSpinner />
                </div>
            );
        }

        if (approvalsQuery.isError) {
            const message = approvalsQuery.error instanceof Error ? `: ${approvalsQuery.error.message}` : '';
            return (
                <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-300">
                    Failed to load approvals{message}.
                </div>
            );
        }

        if (groupedApprovals.length === 0) {
            return (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-400">
                    No pending approvals. Requests from AI Workbench sessions will appear here.
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-6">
                {groupedApprovals.map(([taskKind, approvals]) => (
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" key={taskKind}>
                        <div className="mb-1 flex items-center justify-between gap-2">
                            <div className="text-sm font-semibold capitalize text-white">{humanizeAIWorkLabel(taskKind)}</div>
                            <Badge variant="secondary">{approvals.length}</Badge>
                        </div>
                        <div>
                            {approvals.map((approval) => (
                                <AIWorkApprovalRow
                                    approval={approval}
                                    approveLabel={
                                        approval.approvalType === 'promote_screener' && approval.strategyId != null
                                            ? 'Approve & create variant'
                                            : 'Approve'
                                    }
                                    context={renderApprovalContext(approval)}
                                    disabled={decideMutation.isPending}
                                    isDeciding={decideMutation.isPending && decideMutation.variables?.approval.id === approval.id}
                                    key={approval.id}
                                    onDecide={(_approvalId, status) => decideMutation.mutate({ approval, status })}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (!sessionPolicy.canAccessAdminRoutes) {
        return (
            <UserLayout containerSize="1100px" title="AI Approvals - Project 314">
                <SupportRestrictedFeatureCard
                    description="AI Workbench approvals can only be reviewed by an administrator account."
                    title="AI Approvals Are Unavailable"
                />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1100px" title="AI Approvals - Project 314">
            <div className="mb-4 flex flex-col gap-1">
                <h1 className="text-2xl font-semibold text-white">AI Approvals</h1>
                <p className="text-sm text-zinc-400">
                    Pending approval requests from AI Workbench sessions. Approving a strategy-linked screener recommendation creates its
                    research variant immediately.
                </p>
            </div>
            {renderContent()}
        </UserLayout>
    );
};

export default AIApprovals;
